/**
 * Preview kinds for the unified attachment viewer modal.
 */
export const PreviewKind = {
  PDF: 'pdf',
  IMAGE: 'image',
  TEXT: 'text',
  DOCX: 'docx',
  EXCEL: 'excel',
  EMAIL: 'email',
  UNSUPPORTED: 'unsupported'
};

function fileExtension(filename) {
  if (filename == null || filename === '') return '';
  const base = String(filename).split(/[/\\]/).pop() || '';
  const i = base.lastIndexOf('.');
  return i <= 0 ? '' : base.slice(i).toLowerCase();
}

/**
 * Primary classification from filename extension.
 */
export function getPreviewKindFromFilename(filename) {
  const ext = fileExtension(filename);

  if (ext === '.pdf') return PreviewKind.PDF;
  if (['.png', '.jpg', '.jpeg', '.gif', '.webp', '.bmp', '.svg', '.heic', '.heif'].includes(ext)) return PreviewKind.IMAGE;
  if (['.txt', '.log', '.md', '.json', '.xml'].includes(ext)) return PreviewKind.TEXT;
  if (ext === '.docx') return PreviewKind.DOCX;
  if (['.xlsx', '.xls', '.xlsm', '.xlsb', '.ods', '.csv', '.tsv'].includes(ext)) return PreviewKind.EXCEL;
  if (['.eml', '.msg'].includes(ext)) return PreviewKind.EMAIL;

  return PreviewKind.UNSUPPORTED;
}

/**
 * Refine kind using server-provided Content-Type when the extension is missing or wrong.
 */
export function refinePreviewKindFromMime(kind, mime) {
  if (!mime || typeof mime !== 'string') return kind;
  const m = mime.split(';')[0].trim().toLowerCase();

  if (m === 'application/pdf') return PreviewKind.PDF;
  if (m.startsWith('image/')) return PreviewKind.IMAGE;
  if (m === 'text/csv' || m === 'application/csv') return PreviewKind.EXCEL;
  if (
    m === 'application/vnd.ms-excel' ||
    m === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
    m.includes('spreadsheetml')
  ) {
    return PreviewKind.EXCEL;
  }
  if (m === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') {
    return PreviewKind.DOCX;
  }
  if (
    m === 'message/rfc822' ||
    m === 'application/vnd.ms-outlook' ||
    m === 'application/x-msg' ||
    m === 'application/octet-stream+msg'
  ) {
    return PreviewKind.EMAIL;
  }
  if (m === 'text/plain' || m === 'application/json' || m === 'text/xml' || m === 'application/xml') {
    if (kind === PreviewKind.UNSUPPORTED || kind === PreviewKind.TEXT) return PreviewKind.TEXT;
  }

  return kind;
}

const EXTENSION_MIME = {
  '.pdf': 'application/pdf',
  '.png': 'image/png',
  '.jpg': 'image/jpeg',
  '.jpeg': 'image/jpeg',
  '.gif': 'image/gif',
  '.webp': 'image/webp',
  '.bmp': 'image/bmp',
  '.svg': 'image/svg+xml'
};

function isGenericMime(mime) {
  if (!mime || typeof mime !== 'string') return true;
  const m = mime.split(';')[0].trim().toLowerCase();
  return m === 'application/octet-stream' || m === 'binary/octet-stream';
}

/**
 * Infer MIME from filename when Azure/storage returns a generic type.
 */
export function inferMimeFromFilename(filename, fallback = 'application/octet-stream') {
  const ext = fileExtension(filename);
  return EXTENSION_MIME[ext] || fallback;
}

/**
 * Ensure blob MIME matches filename so iframe/img preview does not trigger a download.
 */
export function coerceBlobForPreview(blob, filename) {
  if (!blob) return blob;
  const inferred = inferMimeFromFilename(filename, '');
  const target =
    isGenericMime(blob.type) && inferred
      ? inferred
      : blob.type || inferred || 'application/octet-stream';
  if (!target || blob.type === target) return blob;
  return new Blob([blob], { type: target });
}

function basenameOnly(name) {
  if (name == null || name === '') return '';
  const s = String(name);
  return s.includes('/') ? s.split('/').pop() : s.includes('\\') ? s.split('\\').pop() : s;
}

/**
 * Pick a user-facing attachment name. Prefers UI original name, then blob name with extension.
 */
export function resolveAttachmentDisplayName(originalFilename, blobName) {
  const uiName = basenameOnly(originalFilename);
  const blobBase = basenameOnly(blobName);
  const genericNames = new Set(['', 'download', 'downloaded-file', 'file']);

  if (uiName && !genericNames.has(uiName.toLowerCase())) {
    if (fileExtension(uiName) || !fileExtension(blobBase)) return uiName;
  }

  if (blobBase) return blobBase;
  return uiName || 'download';
}
